import { Flex, theme, Typography } from 'antd';
import type { GlobalToken } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { QuestionIcon } from '@phosphor-icons/react';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Tooltip } from '../Tooltip';
import type {
  OutOfDatePublicationVariant,
  UseOutOfDatePublicationVariantArgs,
} from './useOutOfDatePublicationVariant';
import { useOutOfDatePublicationAction } from './useOutOfDatePublicationAction';
import { REGENERATE_ITEMS_HINT_KEY } from './copyKeys';
import { radius } from '../../theme/radius';

const KEY = 'components.PublicationStatusBar';

/**
 * Which action applies right now. Same priority as the floating widget's
 * `useOutOfDatePublicationVariant` (stale media first), but derived live
 * rather than frozen: that hook locks onto its first answer because the page
 * unmounts the widget once nothing is stale, whereas this bar stays mounted
 * and has to follow the flags back down after a publish.
 */
const resolveVariant = ({
  isLoading,
  isItemsOutOfDate,
  isPublicationOutOfDate,
}: UseOutOfDatePublicationVariantArgs):
  | OutOfDatePublicationVariant
  | undefined => {
  if (isLoading) return undefined;
  if (isItemsOutOfDate) return 'regenerate';
  if (isPublicationOutOfDate) return 'update';
  return undefined;
};

export interface PublicationStatusBarProps extends UseOutOfDatePublicationVariantArgs {
  /** A save is in flight somewhere on the page — shows the saving indicator. */
  isSaving: boolean;
  /**
   * Whether a live publication exists. Without one there is always something
   * to put live, so "Publish changes" publishes instead of sitting disabled
   * (never published and unpublished alike).
   */
  isLive: boolean;
  /** Regenerates the out-of-date items (media and/or add-ons). */
  onRegenerate: () => Promise<void>;
  /** Publishes, or republishes the live publication. */
  onUpdate: () => Promise<void>;
  className?: string;
}

/**
 * Header status row for an editing page that autosaves: a "Saving changes…"
 * indicator while any save is in flight, and one publication action —
 * "Regenerate add-ons" when the media or an add-on is out of date, "Publish new
 * changes" when the live publication is stale, "Publish changes" (enabled)
 * when nothing is live, and the same, disabled, when the live publication
 * already has everything.
 *
 * The click runs through `useOutOfDatePublicationAction`, the same hook
 * behind the floating `OutOfDatePublicationWidget`; see `resolveVariant` for
 * why the choice of action is derived live here instead.
 */
export const PublicationStatusBar = ({
  isSaving,
  isLoading,
  isLive,
  isItemsOutOfDate,
  isPublicationOutOfDate,
  onRegenerate,
  onUpdate,
  className,
}: PublicationStatusBarProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { token } = theme.useToken();
  const variant = resolveVariant({
    isLoading,
    isItemsOutOfDate,
    isPublicationOutOfDate,
  });
  // No variant means nothing is stale; the only action left is a plain
  // publish, which is the "update" path.
  const { isSubmitting, runAction } = useOutOfDatePublicationAction({
    variant: variant ?? 'update',
    onRegenerate,
    onUpdate,
  });

  const renderAction = () => {
    if (variant === 'regenerate') {
      const hint = t(REGENERATE_ITEMS_HINT_KEY);
      return (
        <Flex align="center" gap={8}>
          <Tooltip content={hint}>
            <IconButton
              variant="tertiary"
              size="m"
              aria-label={hint}
              icon={<QuestionIcon />}
            />
          </Tooltip>
          <Button
            variant="upgrade"
            size="m"
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={runAction}
          >
            {t(`${KEY}.regenerateNew`)}
          </Button>
        </Flex>
      );
    }
    if (variant === 'update') {
      return (
        <Button
          variant="primary"
          size="m"
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={runAction}
        >
          {t(`${KEY}.publish`)}
        </Button>
      );
    }
    // Nothing stale: publish if nothing is live yet, otherwise nothing to do.
    const canPublish = !isLoading && !isLive;
    return (
      <Button
        variant="primary"
        size="m"
        loading={isSubmitting}
        disabled={!canPublish || isSubmitting}
        onClick={canPublish ? runAction : undefined}
      >
        {t(`${KEY}.publish`)}
      </Button>
    );
  };

  return (
    <Flex className={className} align="center" gap={16}>
      {isSaving ? (
        <Flex align="center" gap={6}>
          <SavingDot $token={token} aria-hidden />
          <Typography.Text type="secondary">
            {t(`${KEY}.saving`)}
          </Typography.Text>
        </Flex>
      ) : null}
      {renderAction()}
    </Flex>
  );
};

export default PublicationStatusBar;

const SavingDot = styled.span<{ $token: GlobalToken }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: ${radius.circle};
  background: ${({ $token }) => $token.colorSuccess};
`;
